import React, { useState, useEffect, useRef } from 'react';
import { format, parse, isValid } from 'date-fns';
import './DatePicker.scss';

interface DatePickerProps {
  id: string;
  name: string;
  label: string;
  value: Date | null;
  onChange: (date: Date | null) => void;
  onBlur?: () => void;
  error?: string;
  touched?: boolean;
  minDate?: Date | null;
  maxDate?: Date | null;
}

export const DatePicker: React.FC<DatePickerProps> = ({
  id,
  name,
  label,
  value,
  onChange,
  onBlur,
  error,
  touched,
  minDate,
  maxDate,
}) => {
  const [inputValue, setInputValue] = useState('');
  const hiddenDateInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (value) {
      setInputValue(format(value, 'MM/dd/yyyy'));
    } else {
      setInputValue('');
    }
  }, [value]);

  const normalizeDate = (date: Date): Date => {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let input = e.target.value.replace(/\D/g, '');

    if (input.length >= 2 && input.length < 4) {
      input = input.substring(0, 2) + '/' + input.substring(2);
    } else if (input.length >= 4) {
      input = input.substring(0, 2) + '/' + input.substring(2, 4) + '/' + input.substring(4, 8);
    }

    setInputValue(input);

    if (input.length === 10) {
      try {
        const parsedDate = parse(input, 'MM/dd/yyyy', new Date());
        if (isValid(parsedDate)) {
          onChange(normalizeDate(parsedDate));
        }
      } catch {
        onChange(null);
      }
    } else if (input === '') {
      onChange(null);
    }
  };

  const handleDatePickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value) {
      const [y, m, d] = e.target.value.split('-').map(Number);
      const selectedDate = new Date(y, m - 1, d);
      onChange(normalizeDate(selectedDate));
    } else {
      onChange(null);
    }
  };

  const handleCalendarClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (hiddenDateInputRef.current) {
      const dateInput = hiddenDateInputRef.current as HTMLInputElement & {
        showPicker?: () => void;
      };

      if (dateInput.showPicker) {
        dateInput.showPicker();
      } else {
        dateInput.focus();
        dateInput.click();
      }
    }
  };

  const handleBlur = () => {
    if (inputValue && inputValue.length === 10) {
      try {
        const parsedDate = parse(inputValue, 'MM/dd/yyyy', new Date());
        if (isValid(parsedDate)) {
          setInputValue(format(parsedDate, 'MM/dd/yyyy'));
        } else {
          setInputValue('');
          onChange(null);
        }
      } catch {
        setInputValue('');
        onChange(null);
      }
    }
    onBlur?.();
  };

  return (
    <div className="form-field date-picker">
      <label htmlFor={id}>{label}</label>
      <div className="date-input-wrapper">
        <input
          type="text"
          id={id}
          name={name}
          value={inputValue}
          onChange={handleTextChange}
          onBlur={handleBlur}
          placeholder="MM/DD/YYYY"
          maxLength={10}
          className={`form-input ${touched && error ? 'error' : ''}`}
        />
        <input
          ref={hiddenDateInputRef}
          type="date"
          value={value ? format(value, 'yyyy-MM-dd') : ''}
          onChange={handleDatePickerChange}
          min={minDate ? format(minDate, 'yyyy-MM-dd') : undefined}
          max={maxDate ? format(maxDate, 'yyyy-MM-dd') : undefined}
          className="hidden-date-input"
          tabIndex={-1}
        />
        <button type="button" className="calendar-icon" onClick={handleCalendarClick} tabIndex={-1}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8 2V5M16 2V5M3.5 9.09H20.5M21 8.5V17C21 20 19.5 22 16 22H8C4.5 22 3 20 3 17V8.5C3 5.5 4.5 3.5 8 3.5H16C19.5 3.5 21 8.5 21 8.5Z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M15.6947 13.7002H15.7037M11.9955 13.7002H12.0045M8.29431 13.7002H8.30329M15.6947 17.3002H15.7037M11.9955 17.3002H12.0045M8.29431 17.3002H8.30329"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
      {touched && error && <div className="error-message">{error}</div>}
    </div>
  );
};
